import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Tooltip, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import AttachmentIcon from '@mui/icons-material/Attachment';

import { ButtonUI, CheckboxUI, ConfirmDialogUI, LinearStaticProgressUI } from '../../../Interface';
import productionService from '../../../../service/production.service';
import SzMOpWidgetReasonDialog from './SzMOpWidgetReasonDialog';
import SzMOpWidgetMultiManDialog from './SzMOpWidgetMultiManDialog';
import SzMOpWidgetQuantityDialog from './SzMOpWidgetQuantityDialog';
import SzMOpWidgetGrouppedQuantityDialog from './SzMOpWidgetGrouppedQuantityDialog';
import { NotificationType } from '../../../../config';
import addNotification from '../../../../utils/addNotification';
import colorFinder from '../../../../utils/colorFinder';
import ProductionReceiptDialog from './ProductionReceiptDialog';
import SzMJobMonitorService from '../../../../service/client/szikaMetro/SzMJobMonitor.service';

dayjs.extend(relativeTime);
dayjs.extend(duration);

const SzMOpWidgetJobmonitorCard = ({
  data,
  userId,
  setRefetch,
  checkedIds,
  handleCheckboxChange,
  rows,
  params,
  helperData,
  reasons,
  useGrouppedState,
  setGrouppedOpDialog,
  autoCompleteOptions,
  feedBackDialogDataState,
  getSourceListAttachment,
}) => {
  const { useGroupped } = useGrouppedState;
  const { setFeedbackDialogData } = feedBackDialogDataState;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState(null);
  const [isInterruptionDialogOpen, setIsInterruptionDialogOpen] = useState(false);
  const [isDowntimeDialogOpen, setIsDowntimeDialogOpen] = useState(false);
  const [isEmployeeGroupDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [isQuantityDialogOpen, setIsQuantityDialogOpen] = useState(false);
  const [isGrouppedDialogOpen, setIsGrouppedDialogOpen] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(null);
  const [timer, setTimer] = useState(null);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedReasonCode, setSelectedReasonCode] = useState(null);
  const [disableBtn, setDisableBtn] = useState(false);

  const operation = rows?.find((op) => op.productionoperationid === data.productionoperationid);

  const groupedOperations =
    operation?.groupReportingId != null ? rows.filter((op) => op.groupReportingId === operation.groupReportingId) : [];

  const isGrouped = groupedOperations.length > 1;

  useEffect(() => {
    if (data.productionreportStartDate) {
      if (timer) {
        clearInterval(timer);
      }

      const interval = setInterval(() => {
        const start = dayjs(data.productionreportStartDate);
        const now = dayjs();
        const diff = dayjs.duration(now.diff(start));

        setElapsedTime(
          `${diff.hours().toString().padStart(2, '0')}:${diff.minutes().toString().padStart(2, '0')}:${diff.seconds().toString().padStart(2, '0')}`
        );
      }, 1000);

      setTimer(interval);

      return () => clearInterval(interval);
    } else {
      if (timer) {
        clearInterval(timer);
        setTimer(null);
      }
      setElapsedTime(null);
    }
  }, [data.productionreportStartDate]);

  const preparedness = data.prepQuantity ? Math.round((data.prepQuantity / data.productionQuantity) * 100) : 0;

  const hasQuantity = (payload) => {
    const keys = Object.keys(payload || {});
    const numericKeys = keys.filter((k) => /^\d+$/.test(k));

    if (numericKeys.length > 0) {
      return numericKeys.every((k) => payload[k]?.quantity !== '');
    }

    return payload?.quantity !== '';
  };

  const hasDoseNumber = (payload) => {
    const laserIds = params?.LASER_OPERATION_IDS ? JSON.parse(params.LASER_OPERATION_IDS) : [];

    if (!laserIds || laserIds.length === 0) return true;

    if (Array.isArray(groupedOperations) && groupedOperations.length > 1) {
      const hasLaser = groupedOperations.some((op) => laserIds.includes(op?.operationId));
      return hasLaser ? (payload?.doseNumber ?? '') !== '' : true;
    }

    const isLaser = laserIds.includes(data?.operationId);
    return isLaser ? (payload?.doseNumber ?? '') !== '' : true;
  };

  const handleStartOperation = () => {
    let operationIds = isGrouped
      ? groupedOperations.map((op) => op.productionoperationid)
      : [data.productionoperationid];

    const values = {
      userId: userId,
      createdBy: userId,
      id: checkedIds.length > 0 ? checkedIds : operationIds.length > 1 ? operationIds : data.productionoperationid,
      employees: selectedEmployees,
    };
    setDisableBtn(true);
    productionService
      .createProductionReport(values)
      .then(() => {
        setRefetch(true);
        setSelectedEmployees([]);
        setIsEmployeeDialogOpen(false);
        setShowConfirmDialog(false);
      })
      .finally(() => setDisableBtn(false));
  };

  const handleStopOperation = (interruptionCode = null, downtimeCode = null, dataObj = {}, isContinuation = false) => {
    const operation = rows?.find(
      (row) => row.productionoperationid === data.productionoperationid && data.productionreportStartDate
    );

    let productionReportIds = [];

    if (operation?.groupReportingId) {
      const groupedOperations = rows.filter((row) => row.groupReportingId === operation.groupReportingId);

      productionReportIds = groupedOperations
        .filter((op) => op.productionreportStartDate && op.productionreportId)
        .map((op) => op.productionreportId);
    }

    const values = {
      interruptionCode: interruptionCode || (selectedReasonCode?.trim() ? selectedReasonCode : null),
      downtimeCode,
      userId,
      quantities: dataObj,
      createNew: downtimeCode || isContinuation,
      id: productionReportIds.length > 0 ? productionReportIds : data.productionreportId,
    };
    return productionService.updateProductionReport(values).then(() => {
      setIsDowntimeDialogOpen(false);
      setShowConfirmDialog(false);
      setIsQuantityDialogOpen(false);
      setSelectedReasonCode('');
      setIsGrouppedDialogOpen(false);
      setRefetch(true);
    });
  };

  const handleFinishOperation = (values) => {
    if (!hasQuantity(values)) {
      addNotification({
        content: 'A mennyiség kitöltése kötelező!',
        type: NotificationType.ERROR,
      });
      return;
    }

    if (!hasDoseNumber(values)) {
      addNotification({
        content: 'Az adagszám kitöltése kötelező!',
        type: NotificationType.ERROR,
      });
      return;
    }

    let operationIds = isGrouped
      ? groupedOperations.map((op) => op.productionoperationid)
      : [data.productionoperationid];

    closeAllOpenedProductionReport({
      quantities: values,
      productionStatusId: 'COMPLETED',
      prodOperationId:
        checkedIds?.length > 0
          ? checkedIds
          : operationIds && operationIds?.length > 0
            ? operationIds
            : data.productionoperationid,
      userId,
    });
  };

  const handleInterruptOperation = (interruptionCode = null, values) => {
    if (!hasQuantity(values)) {
      addNotification({
        content: 'A mennyiség kitöltése kötelező!',
        type: NotificationType.ERROR,
      });
      return;
    }

    const operation = rows?.find((op) => op.productionoperationid === data.productionoperationid);

    let operationIds = [data.productionoperationid];

    if (operation?.groupReportingId) {
      const groupedOperations = rows?.filter((op) => op?.groupReportingId === operation?.groupReportingId);
      operationIds = groupedOperations.map((op) => op.productionoperationid);
    }

    closeAllOpenedProductionReport({
      interruptionCode: interruptionCode || (selectedReasonCode?.trim() ? selectedReasonCode : null),
      productionStatusId: 'INTERRUPTED',
      quantities: values,
      prodOperationId:
        checkedIds?.length > 0
          ? checkedIds
          : operationIds && operationIds?.length > 0
            ? operationIds
            : data.productionoperationid,
      userId,
    });
  };

  const openConfirmDialog = () => {
    const operation = rows?.find((row) => row.productionoperationid === data.productionoperationid);

    let groupedOperations = [];
    if (operation?.groupReportingId) {
      groupedOperations = rows.filter((row) => row.groupReportingId === operation.groupReportingId);
    }

    const isGrouped = groupedOperations.length > 1;

    const interimReport = reasons.interimReport;

    if (data.productionreportStartDate && !data.downtimeCode) {
      setConfirmDialogData({
        message: 'Mit szeretne csinálni?',
        confirmButtonText: 'Kész',
        joker1ButtonText: 'Megszakít',
        ...(interimReport && {
          joker2ButtonText: interimReport.name,
        }),
        handleChangeElement: () => {
          setShowConfirmDialog(false);
          if (isGrouped) {
            setIsGrouppedDialogOpen(true);
          } else {
            setIsQuantityDialogOpen(true);
          }
        },
        handleChangeJoker1Element: () => {
          setIsInterruptionDialogOpen(true);
        },
        ...(interimReport && {
          handleChangeJoker2Element: () => {
            setShowConfirmDialog(false);
            setSelectedReasonCode(interimReport.code);

            if (isGrouped) {
              setIsGrouppedDialogOpen(true);
            } else {
              setIsQuantityDialogOpen(true);
            }
          },
        }),
      });
    } else if (data.productionreportStartDate && data.downtimeCode) {
      setConfirmDialogData({
        message: 'Mit szeretne csinálni?',
        confirmButtonText: 'Kész',
        joker1ButtonText: 'Megszakít',
        joker2ButtonText: 'Folytatás',
        handleChangeElement: () => {
          setShowConfirmDialog(false);
          if (isGrouped) {
            setIsGrouppedDialogOpen(true);
          } else {
            setIsQuantityDialogOpen(true);
          }
        },
        handleChangeJoker1Element: () => {
          setIsInterruptionDialogOpen(true);
        },
        handleChangeJoker2Element: () => {
          handleStopOperation(null, null, {}, true);
        },
      });
    } else {
      if (useGroupped) {
        return setGrouppedOpDialog(true);
      }
      setConfirmDialogData({
        message: 'Biztosan el szeretné indítani a gyártási műveletet?',
        confirmButtonText: 'Igen',
        handleChangeElement: () => {
          handleStartClick();
        },
      });
    }
    setShowConfirmDialog(true);
  };

  const handleStartClick = () => {
    if (data.groupReporting && data.multiManOperation > 1) {
      const findedGroup = helperData?.operationGroup?.find((group) =>
        group.operationGroupLink?.some((link) => link.operationId === data.operationId)
      );

      const usersInGroup = findedGroup?.operationGroupUserLink
        ?.filter((link) => !link.user?.groupLeader)
        .map((link) => link.user);

      setEmployeeGroups(usersInGroup);
      setIsEmployeeDialogOpen(true);
      setShowConfirmDialog(false);
    } else {
      handleStartOperation();
    }
  };

  const handleGroupedInterruptOperation = (values) => {
    handleInterruptOperation(selectedReasonCode, values);
  };

  const closeAllOpenedProductionReport = (data) => {
    SzMJobMonitorService.closeAllOpenedProductionReport(data).then(() => {
      setIsDowntimeDialogOpen(false);
      setShowConfirmDialog(false);
      setIsQuantityDialogOpen(false);
      setSelectedReasonCode('');
      setIsGrouppedDialogOpen(false);
      setRefetch(true);
    });
  };

  const handleJoinOperation = (e) => {
    e.stopPropagation();
    SzMJobMonitorService.joinOperation(data.productionoperationid, userId).then(() => {
      setRefetch(true);
    });
  };

  const handleLeaveOperation = (e) => {
    e.stopPropagation();
    SzMJobMonitorService.leaveOperation(data.productionoperationid, userId).then(() => {
      setRefetch(true);
    });
  };

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          borderRadius: 2,
          background: '#E4F0FF',
        }}
      >
        <Grid
          container
          direction="row"
          sx={{
            justifyContent: 'center',
            alignItems: 'stretch',
          }}
          p={0.5}
          columnSpacing={1}
        >
          {/* Left Section */}
          <Grid item xs={2}>
            <Box
              sx={{
                borderRadius: 2,
                boxShadow:
                  ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
                height: '100%',
                padding: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                '&:hover': {
                  background: colorFinder(data.prevStatusColor)?.jobMonitorHoverColor,
                },
                backgroundColor: colorFinder(data.prevStatusColor)?.jobMonitorBgColor,
              }}
            >
              <Typography fontWeight="bold">{data?.prevLabel}</Typography>
              <Typography fontWeight="bold" fontStyle="italic">
                {data?.prevOperationName}
              </Typography>
              <Typography>{data?.prevStatusLabel}</Typography>
            </Box>
          </Grid>

          {/* Center Section */}
          <Grid item xs={7}>
            <Box
              sx={{
                backgroundColor: colorFinder(data.currentStatusColor)?.jobMonitorBgColor,
                borderRadius: 2,
                boxShadow:
                  ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
                padding: 1,
                height: '100%',
                cursor: 'pointer',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#555',
                },

                '&:hover': {
                  background: colorFinder(data.currentStatusColor)?.jobMonitorHoverColor,
                },
              }}
              onClick={() => openConfirmDialog()}
            >
              <Grid container spacing={0}>
                <Grid item xs={12}>
                  {useGroupped &&
                    !data.groupReportingId &&
                    data.groupReporting !== 0 &&
                    data.prodOpStatusId !== 'INPROGRESS' && (
                      <Box onClick={(event) => event.stopPropagation()}>
                        <CheckboxUI
                          label="Csoportos lejelentés"
                          sx={{ margin: 0, padding: '0 0.5rem 0 0' }}
                          checked={checkedIds.includes(data.productionoperationid)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(event) => {
                            handleCheckboxChange(event.target.checked, data.productionoperationid);
                          }}
                        />
                      </Box>
                    )}
                </Grid>
              </Grid>
              <Grid container spacing={0}>
                <Grid item xs={7} display="flex" alignItems="center">
                  <Box
                    backgroundColor="rgba(240, 237, 45, 0.74)"
                    p="0.2rem 0.6rem"
                    mr="0.5rem"
                    borderRadius="12px"
                    onClick={(event) => {
                      event.stopPropagation();
                      setFeedbackDialogData({
                        createdBy: userId,
                        productionId: data.productionid,
                        prodOperationId: data.productionoperationid,
                        prodOperationLabel: data.label,
                        itemCode: data.articleNumber,
                        baseLine: data.customeroerderlinenum,
                        companyId: data.companyId,
                      });
                    }}
                  >
                    <Typography fontWeight="bold">Fb</Typography>
                  </Box>
                  <IconButton
                    sx={{ mr: 1, p: 0 }}
                    onClick={(event) => {
                      event.stopPropagation();
                      getSourceListAttachment(data.articleNumber);
                    }}
                  >
                    <AttachmentIcon
                      sx={{
                        fontSize: '2rem',
                        color: data?.attachedFileExist === 'Yes' && '#0065F8',
                      }}
                    />
                  </IconButton>
                  <Typography fontWeight="bold">
                    {[
                      data.workingNumber,
                      data.reference,
                      data?.finishedItemCode,
                      data.showMaterialItemCode && data.AlapanyagItemCode ? data.AlapanyagItemCode : null,
                    ]
                      .filter(Boolean)
                      .join(' - ')}
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  {data.productionreportStartDate && (
                    <Typography fontWeight="bold">
                      Elindítva: {dayjs(data.productionreportStartDate).format('YYYY-MM-DD HH:mm')}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={2} textAlign="right">
                  <Typography fontWeight="bold" color="blue">
                    {elapsedTime}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={0}>
                <Grid item xs={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Kezdés
                  </Typography>
                  <Typography>{dayjs(data.startDate).format('YYYY-MM-DD')}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Befejezés
                  </Typography>
                  <Typography>{dayjs(data.BefejezesiHatarido).format('YYYY-MM-DD')}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography>{data.articleName}</Typography>
                </Grid>
                <Grid item xs={1}>
                  {data.weight && <Typography>{data?.weight} kg</Typography>}
                </Grid>
                <Grid item xs={2}>
                  <Typography>{data.articleNumber} </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography>{`${data.prepQuantity ?? 0} / ${data.productionQuantity} ${data.articleQtyCode}`}</Typography>
                </Grid>
                <Grid item xs={1} textAlign="right">
                  <Typography>
                    {params?.QUANTITY_UNIT_HOUR_CODE === data.plannedQtCode
                      ? `${Math.round(data.prepTimeSpent * 100) / 100} / ${data.plannedQty} ${data.plannedQtCode}`
                      : `${Math.round(data.prepTimeSpentInMin * 100) / 100} / ${data.plannedQty} ${data.plannedQtCode}`}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={0}>
                <Grid item xs={7}>
                  <Typography>{data?.companyname}</Typography>
                </Grid>
                <Grid item xs={5} textAlign="right">
                  <LinearStaticProgressUI
                    bgColor={data.productionreportStartDate ? '#4ade80' : 'rgba(113, 113, 122, 0.5)'}
                    value={preparedness}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={0} mt="0.5rem">
                {data.groupReporting !== 0 && data.multiManOperation > 1 && data.multi_group_num !== null && (
                  <Grid item xs={1}>
                    <Tooltip
                      title={data.multi_group_res}
                      placement="top"
                      arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            color: '#514E6A',
                            backgroundColor: '#ffff',
                            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                            maxWidth: 400,
                            fontSize: '1rem',
                          },
                        },
                        arrow: {
                          sx: {
                            color: '#ffff',
                          },
                        },
                      }}
                    >
                      <Box sx={{ border: '2px solid black', borderRadius: '15px', width: '30px', textAlign: 'center' }}>
                        {data.multi_group_num}
                      </Box>
                    </Tooltip>
                  </Grid>
                )}
                <Grid item xs={3}>
                  <Typography fontWeight="bold" fontStyle="italic">
                    {data.operationName}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography>{data.productionoperationComment}</Typography>
                </Grid>
                <Grid item xs={3} textAlign="center">
                  {data?.downtimeCode && (
                    <Typography fontWeight="bold" sx={{ background: '#FF6363', borderRadius: '8px' }}>
                      Állás oka: {data?.downtimeName}
                    </Typography>
                  )}
                  {data.interruptionCode && (
                    <Typography fontWeight="bold" sx={{ background: '#FF6363', borderRadius: '8px' }}>
                      Megszakítás oka: {data.interruptionName}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={2} textAlign="right">
                  <Typography>{data.productionOperationStatus}</Typography>
                </Grid>
              </Grid>
              <Grid container spacing={0} mt="0.5rem">
                <Grid item xs={8}>
                  <Typography>{data.reportingBy}</Typography>
                </Grid>
                {data.prodOpStatusId === 'INPROGRESS' && (
                  <Grid item xs={4}>
                    <Box justifyContent="end" display="flex" gap="0.5rem" flexWrap="wrap">
                      <ButtonUI
                        text="Csatlakozás"
                        size="sm"
                        className="bg-warning"
                        onClick={(e) => handleJoinOperation(e)}
                      />
                      <ButtonUI
                        text="Lecsatlakozás"
                        size="sm"
                        className="bg-danger"
                        onClick={(e) => handleLeaveOperation(e)}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>

          {/* Right Section */}
          <Grid item xs={1.5}>
            <Box
              sx={{
                borderRadius: 2,
                height: '100%',
                padding: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                boxShadow:
                  ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
                backgroundColor: colorFinder(data.nextStatus1Color)?.jobMonitorBgColor,
                '&:hover': {
                  background: colorFinder(data.nextStatus1Color)?.jobMonitorHoverColor,
                },
              }}
            >
              <Typography fontWeight="bold">{data?.next1Label}</Typography>
              <Typography fontWeight="bold" fontStyle="italic">
                {data?.next1OperationName}
              </Typography>
              <Typography>{data?.nextStatus1Label}</Typography>
            </Box>
          </Grid>
          <Grid item xs={1.5}>
            <Box
              sx={{
                borderRadius: 2,
                height: '100%',
                padding: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                boxShadow:
                  ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
                backgroundColor: colorFinder(data.nextStatus2Color)?.jobMonitorBgColor,
                '&:hover': {
                  background: colorFinder(data.nextStatus2Color)?.jobMonitorHoverColor,
                },
              }}
            >
              <Typography fontWeight="bold">{data?.next2Label}</Typography>
              <Typography fontWeight="bold" fontStyle="italic">
                {data?.next2OperationName}
              </Typography>
              <Typography>{data?.nextStatus2Label}</Typography>
            </Box>
          </Grid>
          <ConfirmDialogUI
            message={confirmDialogData?.message}
            confirmButtonText={confirmDialogData?.confirmButtonText}
            joker1ButtonText={confirmDialogData?.joker1ButtonText}
            joker2ButtonText={confirmDialogData?.joker2ButtonText}
            handleChangeElement={confirmDialogData?.handleChangeElement}
            handleChangeJoker1Element={confirmDialogData?.handleChangeJoker1Element}
            handleChangeJoker2Element={confirmDialogData?.handleChangeJoker2Element}
            show={showConfirmDialog}
            onHide={() => setShowConfirmDialog(false)}
            largeButtons={true}
            maxWidth="md"
          />
          <SzMOpWidgetReasonDialog
            open={isInterruptionDialogOpen}
            handleClose={() => setIsInterruptionDialogOpen(false)}
            data={reasons.interruption}
            setSelectedReasonCode={setSelectedReasonCode}
            setIsQuantityDialogOpen={setIsQuantityDialogOpen}
            setShowConfirmDialog={setShowConfirmDialog}
            handleInterruptOperation={handleInterruptOperation}
            isGrouped={isGrouped}
            mode="interrupt"
            setIsGrouppedDialogOpen={setIsGrouppedDialogOpen}
          />
          <SzMOpWidgetReasonDialog
            open={isDowntimeDialogOpen}
            handleClose={() => setIsDowntimeDialogOpen(false)}
            data={reasons.downtime}
            handleStopOperation={handleStopOperation}
            mode="downtime"
          />
          <SzMOpWidgetMultiManDialog
            open={isEmployeeGroupDialogOpen}
            handleClose={() => setIsEmployeeDialogOpen(false)}
            data={employeeGroups}
            setSelectedEmployees={setSelectedEmployees}
            handleStartOperation={handleStartOperation}
            disableBtn={disableBtn}
          />
          {!data?.stockAccounting ? (
            <SzMOpWidgetQuantityDialog
              open={isQuantityDialogOpen}
              handleClose={() => setIsQuantityDialogOpen(false)}
              handleFinishOperation={handleFinishOperation}
              handleInterruptOperation={handleInterruptOperation}
              mode={selectedReasonCode ? 'interrupt' : 'finish'}
              autoCompleteOptions={autoCompleteOptions}
              helperData={helperData}
              data={data}
            />
          ) : (
            <ProductionReceiptDialog
              open={isQuantityDialogOpen}
              onClose={() => setIsQuantityDialogOpen(false)}
              handleFinishOperation={handleFinishOperation}
              handleInterruptOperation={handleInterruptOperation}
              mode={selectedReasonCode ? 'interrupt' : 'finish'}
              onSave={(data) => {
                setIsQuantityDialogOpen(false);
              }}
              autocompleteData={helperData}
              cardData={data}
              autoCompleteOptions={autoCompleteOptions}
            />
          )}
          <SzMOpWidgetGrouppedQuantityDialog
            open={isGrouppedDialogOpen}
            handleClose={() => setIsGrouppedDialogOpen(false)}
            data={groupedOperations}
            handleFinishOperation={selectedReasonCode ? handleGroupedInterruptOperation : handleFinishOperation}
            autoCompleteOptions={autoCompleteOptions}
            helperData={helperData}
            mode={selectedReasonCode ? 'interrupt' : 'finish'}
          />
        </Grid>
      </Box>
    </Grid>
  );
};

export default SzMOpWidgetJobmonitorCard;
